import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useFormContext} from "react-hook-form";
import styled, {css} from "styled-components";
import {faCheckDouble} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {isMobileDevice} from "../../utils/responsive.js";

// Components
import InputError from "./InputError.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {pad} from "../../style/components/variables.js";
import {Abbr, FormFieldWrapper, Input, Label, Pill, Text} from "../../style/components/general.js";

const InputDate = ({
  testId = "input-date",
  label,
  name,
  required = false,
  disabled = false,
  defaultValue = "",
  placeholder,
  inline = false,
  maxWidth,
  condition,
  prompt,
  tag,
  min,
  max,
  shared = false,
  linkedAcrossStates = false,
}) => {
  const isMounted = useMountedState();

  const [focused, setFocused] = useState(false);

  const {
    register,
    watch,
    setValue,
    formState: {errors}
  } = useFormContext();
  const value = watch(name);

  useEffect(() => {
    if (isMounted() && !value) setValue(name, defaultValue);
  }, [isMounted, name, value, defaultValue, setValue]);

  return (
    <FormFieldWrapper selfInline={inline}>
      {label && (
        <FieldLabel htmlFor={name} bold inline>
          {label.toUpperCase()}
          {required && "*"}
          {tag && (
            <Pill quiet>
              <Abbr title={tag}>{tag}</Abbr>
            </Pill>
          )}
          <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
          {condition && <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>}
        </FieldLabel>
      )}
      {prompt && <Prompt quiet>{prompt}</Prompt>}
      <Date
        id={name}
        data-testid={testId}
        type={value || focused || !placeholder ? "date" : "text"}
        min={min}
        max={max}
        name={name}
        disabled={disabled}
        maxWidth={maxWidth}
        placeholder={placeholder}
        hasValue={value}
        isMobile={isMobileDevice() ? 1 : 0}
        onFocus={() => setFocused(true)}
        {...register(name, {required: required, onBlur: () => setFocused(false)})}
      />
      <InputError errors={errors} name={name} />
    </FormFieldWrapper>
  );
};

InputDate.propTypes = {
  testId: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  prompt: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  inline: PropTypes.bool,
  maxWidth: PropTypes.string,
  condition: PropTypes.string,
  tag: PropTypes.string,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.instanceOf(Element)})
  ])
};

// Style Overrides
const FieldLabel = styled(Label)`
  white-space: normal;
  gap: ${pad / 2}px;
`;

const Prompt = styled(Text)`
  margin-bottom: ${pad / 2}px;
`;

const Date = styled(Input)`
  min-width: 135px;
  max-width: 135px;
  height: 35px;

  ${({maxWidth}) =>
    maxWidth &&
    css`
      max-width: ${maxWidth};
    `}
`;

export default InputDate;
